<template>
    <a-modal
        :visible="visible"
        :title="isEditing ? 'Kuralı Düzenle' : 'Yeni Kural Oluştur'"
        width="900px"
        :footer="null"
        @cancel="onCancel"
        :bodyStyle="{ padding: '24px' }"
    >
        <div class="rule-editor">
            <!-- Steps -->
            <a-steps :current="currentStep" size="small" class="steps-container">
                <a-step title="Ne Zaman?" />
                <a-step title="Koşullar" />
                <a-step title="Aksiyonlar" />
            </a-steps>

            <!-- Step Content -->
            <div class="step-content">
                <!-- Step 1: WHEN -->
                <div v-if="currentStep === 0" class="when-step">
                    <h3>Kural Ne Zaman Çalışsın?</h3>
                    
                    <div class="rule-name-input">
                        <label>Kural Adı</label>
                        <a-input
                            v-model="localRule.name"
                            placeholder="Kural için bir isim girin"
                            size="large"
                        />
                    </div>

                    <div class="when-options">
                        <div
                            class="when-option"
                            :class="{ selected: localRule.when === 'contact_created' }"
                            @click="localRule.when = 'contact_created'"
                        >
                            <div class="option-icon">
                                <i class="material-icons">person_add</i>
                            </div>
                            <div class="option-content">
                                <h4>Müşteri Kartı Oluştuğunda</h4>
                                <p>Yeni bir müşteri kaydı oluşturulduğunda çalışır</p>
                            </div>
                            <div class="option-check">
                                <i v-if="localRule.when === 'contact_created'" class="material-icons">check_circle</i>
                            </div>
                        </div>

                        <div
                            class="when-option"
                            :class="{ selected: localRule.when === 'chat_opened' }"
                            @click="localRule.when = 'chat_opened'"
                        >
                            <div class="option-icon">
                                <i class="material-icons">chat</i>
                            </div>
                            <div class="option-content">
                                <h4>Chat Açıldığında</h4>
                                <p>Müşteri yeni bir konuşma başlattığında çalışır</p>
                            </div>
                            <div class="option-check">
                                <i v-if="localRule.when === 'chat_opened'" class="material-icons">check_circle</i>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Step 2: IF (Conditions) -->
                <div v-if="currentStep === 1" class="if-step">
                    <h3>Hangi Koşullar Sağlanmalı?</h3>
                    <FilterBuilder
                        v-model="localRule.conditions"
                        :when-type="localRule.when"
                    />
                </div>

                <!-- Step 3: ACTIONS -->
                <div v-if="currentStep === 2" class="actions-step">
                    <h3>Hangi Aksiyonlar Çalışsın?</h3>
                    <ActionsEditor
                        v-model="localRule.actions"
                        :when-type="localRule.when"
                    />
                </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="step-navigation">
                <a-button
                    v-if="currentStep > 0"
                    @click="currentStep--"
                >
                    Geri
                </a-button>
                <div class="spacer"></div>
                <a-button @click="onCancel">
                    İptal
                </a-button>
                <a-button
                    v-if="currentStep < 2"
                    type="primary"
                    @click="currentStep++"
                    :disabled="!canProceed"
                >
                    İleri
                </a-button>
                <a-button
                    v-if="currentStep === 2"
                    type="primary"
                    @click="onSave"
                >
                    Kaydet
                </a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import FilterBuilder from "./components/FilterBuilder.vue";
import ActionsEditor from "./components/ActionsEditor.vue";
import log2 from "../../../../../utils/log2";

const log = log2("RuleEditor");

export default {
    name: "RuleEditor",
    components: {
        FilterBuilder,
        ActionsEditor
    },
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        rule: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            currentStep: 0,
            localRule: null
        };
    },
    computed: {
        isEditing() {
            return this.rule && this.rule.id;
        },
        canProceed() {
            if (this.currentStep === 0) {
                return this.localRule.name && this.localRule.name.trim().length > 0;
            }
            return true;
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.initializeLocalRule();
                this.currentStep = 0;
            }
        }
    },
    created() {
        this.initializeLocalRule();
    },
    methods: {
        initializeLocalRule() {
            if (this.rule) {
                const ruleCopy = JSON.parse(JSON.stringify(this.rule));
                // Date'leri string formatına çevir
                if (ruleCopy.createdAt && typeof ruleCopy.createdAt !== 'string') {
                    ruleCopy.createdAt = new Date(ruleCopy.createdAt).toISOString();
                }
                if (ruleCopy.updatedAt && typeof ruleCopy.updatedAt !== 'string') {
                    ruleCopy.updatedAt = new Date(ruleCopy.updatedAt).toISOString();
                }
                this.localRule = ruleCopy;
            } else {
                this.localRule = {
                    id: this.generateUUID(),
                    name: "Yeni Kural",
                    enabled: true,
                    order: 0,
                    when: "contact_created",
                    conditions: {
                        filterGroups: [],
                        groupOperator: "AND"
                    },
                    actions: [],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
            }
        },

        onCancel() {
            this.$emit("close");
        },

        onSave() {
            this.localRule.updatedAt = new Date().toISOString();
            // createdAt da string formatında olmalı
            if (this.localRule.createdAt && typeof this.localRule.createdAt !== 'string') {
                this.localRule.createdAt = new Date(this.localRule.createdAt).toISOString();
            }
            this.$emit("save", this.localRule);
        },

        generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.rule-editor {
    .steps-container {
        margin-bottom: 32px;
    }

    .step-content {
        min-height: 400px;
        padding: 24px 0;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #1a1a1a;
            margin: 0 0 24px 0;
        }

        .when-step {
            .rule-name-input {
                margin-bottom: 32px;

                label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: #1a1a1a;
                }
            }

            .when-options {
                display: flex;
                flex-direction: column;
                gap: 16px;

                .when-option {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 20px;
                    border: 2px solid #e8e8e8;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s ease;

                    &:hover {
                        border-color: #1890ff;
                        background: #f5f5f5;
                    }

                    &.selected {
                        border-color: #1890ff;
                        background: #e6f7ff;
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);

                        .option-icon {
                            color: #1890ff;
                        }

                        .option-check {
                            color: #1890ff;
                        }
                    }

                    .option-icon {
                        .material-icons {
                            font-size: 48px;
                            color: #999;
                        }
                    }

                    .option-content {
                        flex: 1;

                        h4 {
                            font-size: 16px;
                            font-weight: 600;
                            margin: 0 0 4px 0;
                            color: #1a1a1a;
                        }

                        p {
                            font-size: 14px;
                            color: #666;
                            margin: 0;
                        }
                    }

                    .option-check {
                        .material-icons {
                            font-size: 32px;
                            color: #e8e8e8;
                        }
                    }
                }
            }
        }
    }

    .step-navigation {
        display: flex;
        gap: 12px;
        padding-top: 24px;
        border-top: 1px solid #f0f0f0;

        .spacer {
            flex: 1;
        }
    }
}
</style>

